import { setGlobalContextProperty, setUser, clearUser } from "@jsq/observability";
import { NavBar } from "apps/portal/Portal/common/NavBar";
import { lpPortalClient } from "common/data/graphql/client";
import { toDbId } from "common/helpers/globalId";
import { identifyHeapUser, resetHeapIdentity } from "common/config/initHeap";
import { UpscopeProvider } from "@upscopeio/react";
import {
  FeatureFlagProvider as UnleashFeatureFlagProvider,
  useIdentifyArena,
  useUpdateContext,
} from "@jsq/flags-react";
import {
  getLpPortalAccountActivationPathNew,
  getLpPortalTermsAndPrivacyPath,
  getSingleManagerPortalPath,
  getUnifiedPortalPP,
  getUnifiedPortalTouBasePath,
  LP_PORTAL_BASE_PATH,
} from "common/helpers/url";
import withDataProvider from "common/hocs/withDataProvider";
import {
  useGetArenaContentQuery,
  useGetArenaFeatureFlagsQuery,
  useGetPortalAppContextQuery,
  useGetPortalFeatureFlagsQuery,
} from "common/types/mainHooks";
import { useGetCaseArenaByProfileQuery } from "common/types/federation/supergraphHooks";
import { ComplianceExternalObjectType } from "common/types/federation/supergraphEnums";
import queryString from "query-string";
import history from "common/utils/history";
import lazyLoad from "common/utils/lazyLoad";
import Router from "common/utils/router";
import { ErrorBoundary, Loader, ToastContainer } from "@jsq/ds/core";
import { Layout } from "@jsq/ds/deprecated";
import { Redirect, Route, Switch, useLocation, useRouteMatch } from "react-router-dom";

import DuplicateSession from "apps/portal/Portal/SingleManager/Spoofing/Error/DuplicateSession";
import SpoofBanner from "apps/portal/Portal/SingleManager/Spoofing/Banner";
import "apps/portal/Portal/styles.scss";
import React, { Suspense, useCallback, useRef } from "react";
import { federationGatewayClient } from "common/data/graphql/federation-gateway-client";
import {
  DocumentCollectorGuest,
  ComplianceRemediation,
  ComplianceRefreshComponent,
  ComplianceDocumentExpiry,
} from "@jsq/compliance-components";
import { FederatedComponent } from "common/helpers/federatedModules/FederatedComponent";
import { RemocalWidget, shouldShowRemocalWidget } from "@jsq/common-components";
import { useZendeskMessagingWidget } from "common/utils/zendeskMessagingWidget";
import { setGpFavicons } from "./common/utils";
import { AppContext } from "./common/AppContext";
import { FeaturesProvider } from "./common/contexts/FeaturesContext";
import { ActivationProtectedRoute } from "./common/ActivationProtectedRoute";
import { PrivateRoute } from "./common/PrivateRoute";
import TermsAndPolicy from "./common/TermsAndPolicy";
import {
  type DateFormatType,
  type SingleManagerContextRouteParams,
  type TimeFormatType,
} from "./common/types";
import { getCurrentApplication } from "./utils";
import { RouteWithDocumentTitle } from "./common/RouteWithDocumentTitle";
import { DEFAULT_NAVBAR_ITEMS, PAGE_TITLES } from "./common/constants";
import OnboardingRequiredProtectedRoute from "./common/OnboardingRequiredProtectedRoute";
import { NotFoundPageV2 } from "./common/NotFoundPageV2";

const LoginPage = lazyLoad(() =>
  import(/* webpackChunkName: "Portal.LoginPage" */ "./common/authentication/LoginPage").then(
    (module) => ({
      default: module.LoginPage,
    }),
  ),
);

const ActivateAccountPage = lazyLoad(
  () =>
    import(/* webpackChunkName: "Portal.ActivateAccountPage" */ "./common/ActivateAccountPage"),
);

const ResetPasswordPage = lazyLoad(() =>
  import(
    /* webpackChunkName: "Portal.ResetPasswordPage" */ "./common/authentication/ResetPasswordPage"
  ).then((module) => ({
    default: module.ResetPasswordPage,
  })),
);

const DataRoom = lazyLoad(() =>
  import(/* webpackChunkName: "Portal.DataRoom" */ "./SingleManager/DataRoom").then(
    (module) => ({
      default: module.DataRoom,
    }),
  ),
);

const DataRoomDocuments = lazyLoad(() =>
  import(
    /* webpackChunkName: "Portal.DataRoom.Documents" */ "./SingleManager/DataRoom/Documents"
  ).then((module) => ({
    default: module.DataRoomDocumentsPage,
  })),
);

const MultiManager = lazyLoad(() =>
  import(/* webpackChunkName: "Portal.MultiManager" */ "./MultiManager").then((module) => ({
    default: module.MultiManager,
  })),
);

const SingleManager = lazyLoad(() =>
  import(/* webpackChunkName: "Portal.SingleManager" */ "./SingleManager").then((module) => ({
    default: module.SingleManager,
  })),
);

const PreviewPage = lazyLoad(() =>
  import(/* webpackChunkName: "Portal.Preview" */ "./common/Preview").then((module) => ({
    default: module.default,
  })),
);

const TaxPreferences = lazyLoad(() =>
  import(/* webpackChunkName: "Portal.TaxPreferences" */ "./SingleManager/TaxPreferences").then(
    (module) => ({
      default: module.TaxPreferences,
    }),
  ),
);

const InvestorQuestionnaireMicroUi = FederatedComponent(
  "/s/dynamic-subs-micro-ui/remoteEntry.js",
  "DynamicSubscriptionsMicroUI",
  "./LpInvestorQuestionnaire",
);

const SetUpTwoFA = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "Portal.common.AccountActivation.SetUpTwoFA" */
      "./common/AccountActivation/SetUpTwoFA"
    ),
);

const UpgradeTwoFASetup = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "Portal.common.AccountActivation.UpgradeTwoFASetup" */
      "./common/AccountActivation/UpgradeTwoFASetup"
    ),
);

const UpgradeTwoFAVerify = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "Portal.common.AccountActivation.UpgradeTwoFAVerify" */
      "./common/AccountActivation/UpgradeTwoFAVerify"
    ),
);

const VerifyTwoFA = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "Portal.common.AccountActivation.VerifyTwoFA" */
      "./common/AccountActivation/VerifyTwoFA"
    ),
);

const LinkVerifyTwoFA = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "Portal.common.AccountActivation.LinkVerifyTwoFA" */
      "./common/AccountActivation/LinkVerifyTwoFA"
    ),
);

const NewManagersList = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "Portal.common.AccountActivation.NewManagersList" */
      "./common/AccountActivation/NewManagersList"
    ),
);

const NewManagerInvitations = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "Portal.common.AccountActivation.NewManagerInvitations" */
      "./common/AccountActivation/NewManagerInvitations"
    ),
);

const RecoveryCodes = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "Portal.common.AccountActivation.RecoveryCodes" */
      "./common/AccountActivation/RecoveryCodes"
    ),
);

const EditLPProfile = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "Portal.common.AccountActivation.RecoveryCodes" */
      "./common/AccountActivation/EditLPProfile"
    ),
);

const SecondaryKycMicroUi = FederatedComponent(
  "/s/dynamic-subs-micro-ui/remoteEntry.js",
  "DynamicSubscriptionsMicroUI",
  "./SecondaryKyc",
);

const navItems = ["home", "documents", "offerings", "emails"];

function PortalWrapper() {
  const match = useRouteMatch<SingleManagerContextRouteParams>("/i/:arenaDomain");
  const refreshRouteMatch = useRouteMatch<{ profileGuid: string }>([
    "/i/profile-refresh/:profileGuid",
    "/i/document-refresh/:profileGuid",
  ]);
  const { search, pathname } = useLocation();
  const identifyArena = useIdentifyArena();
  const updateFeatureFlagContext = useUpdateContext();
  const identifiedUser = useRef<string | undefined>(undefined);
  const identifiedArena = useRef<string | undefined>(undefined);

  const arenaDomain = match?.params?.arenaDomain;
  const profileGuid = refreshRouteMatch?.params?.profileGuid;

  const { data: featureFlagData } = useGetPortalFeatureFlagsQuery({
    context: { unauthenticated: true },
  });
  const { data: arenaContentData, loading: arenaContentLoading } = useGetArenaContentQuery({
    variables: {
      arenaDomain: arenaDomain || "",
    },
    context: { unauthenticated: true },
    skip: !arenaDomain,
  });
  const { data: caseArenaData } = useGetCaseArenaByProfileQuery({
    variables: {
      externalObject: {
        objectId: profileGuid ?? "",
        objectTypeEnum: ComplianceExternalObjectType.ComplianceProfile,
        service: "main",
      },
    },
    client: federationGatewayClient,
    skip: !profileGuid,
  });
  const { refetch: zendeskRefetch } = useZendeskMessagingWidget();

  /**
   * A code is added for authentication for a secondary subscriber
   */
  const code = queryString.parse(search)?.code;

  const {
    data: portalAppData,
    loading,
    refetch,
  } = useGetPortalAppContextQuery({
    skip: pathname.includes("secondary-kyc") && Boolean(code),
  });

  const isAuthenticated = Boolean(portalAppData?.currentGlobalUser);

  // Only fetch arena feature flags once authenticated so unauthenticated
  // callers can't enumerate which arena features are enabled.
  const { data: arenaFlagsData } = useGetArenaFeatureFlagsQuery({
    variables: { arenaDomain: arenaDomain || "" },
    skip: !isAuthenticated,
  });

  const handleLogin = useCallback(async () => {
    await refetch();
    await zendeskRefetch();
  }, [refetch, zendeskRefetch]);

  if (
    !featureFlagData ||
    loading ||
    arenaContentLoading ||
    (isAuthenticated && !arenaFlagsData)
  ) {
    return null;
  }
  const managerArenas = portalAppData
    ? portalAppData.managerArenas.map((a) => ({
        id: a.id,
        name: a.name,
        secondLevelDomain: a.secondLevelDomain,
        logoUrl: a.logoUrl || undefined,
        logoBackgroundColor:
          (a.useBgColorForPortalIcons ? a.logoBackgroundColor : undefined) || undefined,
        supportEmail: a.supportEmail,
        perfMetricsAsOfDate: a.perfMetricsAsOfDate,
        allowPortalInvestorReport: a.allowPortalInvestorReport,
        showDisregardedEntityQuestionnaire: a.showDisregardedEntityQuestionnaire,
        paymentPermission: a.paymentPermission || null,
        labelDisplaySettings:
          a.labelDisplaySettings?.map((setting) => ({
            id: setting.id,
            fieldName: setting.fieldName ?? null,
            singular: setting.singular ?? null,
            plural: setting.plural ?? null,
            variants:
              setting.variants?.map((v) => ({
                id: v?.id ?? null,
                variantName: v?.variantName ?? null,
                investmentEntityIds: v?.investmentEntities?.map((e) => toDbId(e.id)) ?? null,
              })) ?? null,
          })) ?? null,
      }))
    : [];

  const onlyOneGp = managerArenas.length === 1;

  const user = portalAppData?.currentGlobalUser
    ? {
        id: portalAppData.currentGlobalUser.id,
        fullName: portalAppData.currentGlobalUser.fullName || "",
        emailAddress: portalAppData.currentGlobalUser.emailAddress,
        dateFormat: portalAppData.currentGlobalUser.dateFormat as DateFormatType,
        timeFormat: portalAppData.currentGlobalUser.timeFormat as TimeFormatType,
        hasActiveSecurityTriggers: portalAppData.currentGlobalUser.hasActiveSecurityTriggers,
      }
    : undefined;

  const unauthenticatedArena = arenaContentData?.unauthenticatedArena;
  const currentArenaContent = unauthenticatedArena
    ? {
        name: unauthenticatedArena.name,
        arenaDomain: arenaDomain || "",
        headerLogoUrl: unauthenticatedArena.headerLogoUrl ?? undefined,
        headerLogoUrl2x: unauthenticatedArena.headerLogoUrl2x ?? undefined,
        contactEmail: unauthenticatedArena.contactEmail ?? undefined,
        contactPhone: unauthenticatedArena.contactPhone ?? undefined,
      }
    : undefined;

  if (user?.id) {
    const userId = toDbId(user.id).toString();
    if (identifiedUser.current !== userId) {
      identifiedUser.current = userId;
      setUser({
        id: userId,
      });
      identifyHeapUser(userId);
      updateFeatureFlagContext({
        userId,
      });
    }
  } else if (identifiedUser.current !== undefined) {
    identifiedUser.current = undefined;
    clearUser();
    resetHeapIdentity();
    updateFeatureFlagContext({
      userId: undefined,
    });
  }

  let arenaId: string;
  let arenaName: string;
  if (onlyOneGp && managerArenas?.[0]) {
    arenaId = toDbId(managerArenas[0].id).toString();
    arenaName = managerArenas[0].name;
    if (identifiedArena.current !== arenaId) {
      identifiedArena.current = arenaId;
      setGlobalContextProperty("arenaId", arenaId);
      setGlobalContextProperty("arenaName", arenaName);
      identifyArena(arenaId);
    }
  } else if (unauthenticatedArena) {
    arenaId = toDbId(unauthenticatedArena.id).toString();
    arenaName = unauthenticatedArena.name;
    if (identifiedArena.current !== arenaId) {
      identifiedArena.current = arenaId;
      setGlobalContextProperty("arenaId", arenaId);
      setGlobalContextProperty("arenaName", arenaName);
      identifyArena(arenaId);
    }
  }

  const spoofingDefaultRedirect = `/i/${managerArenas?.[0]?.name?.toLowerCase()}`;

  setGpFavicons(unauthenticatedArena?.faviconUrl16px, unauthenticatedArena?.faviconUrl32px);

  const getPath = (path: string) => {
    const singleManager = !!currentArenaContent;
    if (singleManager && onlyOneGp && currentArenaContent.arenaDomain) {
      return `${getSingleManagerPortalPath(currentArenaContent.arenaDomain)}/${path}`;
    }
    return `${LP_PORTAL_BASE_PATH}/${path}`;
  };

  const DocumentCollectorGuestWrapper = withDataProvider(() => {
    return <DocumentCollectorGuest />;
  }, federationGatewayClient);

  const ComplianceRemediationWrapper = withDataProvider(() => {
    return (
      <ComplianceRemediation
        arenaId={Number(arenaId)}
        userFullName={user?.fullName || user?.emailAddress}
        errorComponent={<NotFoundPageV2 arenaName={arenaName} />}
      />
    );
  }, federationGatewayClient);

  const profileArenaName = caseArenaData?.complianceCaseArenaByProfile?.arenaName;
  const profileArenaId = caseArenaData?.complianceCaseArenaByProfile?.arenaId;

  const ComplianceRefreshComponentWrapper = withDataProvider(() => {
    return (
      <ComplianceRefreshComponent
        arenaId={profileArenaId ?? Number(arenaId)}
        errorComponent={<NotFoundPageV2 arenaName={profileArenaName ?? arenaName} />}
      />
    );
  }, federationGatewayClient);

  const ComplianceDocumentExpiryWrapper = withDataProvider(() => {
    return (
      <ComplianceDocumentExpiry
        arenaId={profileArenaId ?? Number(arenaId)}
        errorComponent={<NotFoundPageV2 arenaName={profileArenaName ?? arenaName} />}
      />
    );
  }, federationGatewayClient);

  const routeProvider = (
    <>
      <ToastContainer />
      {shouldShowRemocalWidget() && <RemocalWidget />}
      <Switch>
        {/* Login routes */}
        <RouteWithDocumentTitle path="/login" documentTitle={PAGE_TITLES.Login}>
          <LoginPage onSuccess={handleLogin} />
        </RouteWithDocumentTitle>
        <RouteWithDocumentTitle path="/password_reset" documentTitle={PAGE_TITLES.Login}>
          <ResetPasswordPage />
        </RouteWithDocumentTitle>
        <Route path={getLpPortalAccountActivationPathNew()}>
          <ActivateAccountPage />
        </Route>
        <Route exact path="/networked_portal/terms-and-privacy/">
          <Redirect to={getUnifiedPortalTouBasePath()} />
        </Route>
        <Route path={["/networked_portal/terms-and-privacy/:arena", getUnifiedPortalPP()]}>
          <Layout
            header={
              <NavBar baseUrl={pathname} navItems={DEFAULT_NAVBAR_ITEMS} isNotAuthenticated />
            }
          >
            <TermsAndPolicy
              token={(queryString.parse(search)?.activation as string) || ""}
              isAuthenticated={false}
            />
          </Layout>
        </Route>
        {/* Unauthenticated routes */}
        <Route exact path="/i/:arenaDomain/compliance-document-upload">
          <DocumentCollectorGuestWrapper />
        </Route>
        <Route exact path="/i/:arenaDomain/secondary-kyc">
          <Suspense fallback={<Loader loading />}>
            <SecondaryKycMicroUi />
          </Suspense>
        </Route>
        <Route exact path="/i/:arenaDomain/offering/:dataRoomKey">
          <ActivationProtectedRoute>
            <OnboardingRequiredProtectedRoute>
              <DataRoom />
            </OnboardingRequiredProtectedRoute>
          </ActivationProtectedRoute>
        </Route>
        <Route exact path="/i/:arenaDomain/offering/:dataRoomKey/documents/:folderId?">
          <ActivationProtectedRoute>
            <OnboardingRequiredProtectedRoute>
              <DataRoomDocuments />
            </OnboardingRequiredProtectedRoute>
          </ActivationProtectedRoute>
        </Route>
        <Route path="/i/:arenaDomain/subscription/:dataRoomKey/">
          <ActivationProtectedRoute>
            <OnboardingRequiredProtectedRoute>
              <Suspense fallback={<Loader loading />}>
                <div data-testid="subscriptions-microui-wrapper">
                  <SpoofBanner />
                  <InvestorQuestionnaireMicroUi />
                </div>
              </Suspense>
            </OnboardingRequiredProtectedRoute>
          </ActivationProtectedRoute>
        </Route>
        <Route path="/i/:arenaDomain/documents/preview/:id">
          <ActivationProtectedRoute>
            <PreviewPage />
          </ActivationProtectedRoute>
        </Route>
        <Route path="/i/:arenaDomain/position/:positionId/documents/preview/:id">
          <ActivationProtectedRoute>
            <PreviewPage />
          </ActivationProtectedRoute>
        </Route>
        {featureFlagData.devFeatures.taxDocDeliveryPreferences && (
          <Route path="/i/:arenaDomain/tax/delivery_preferences/:id">
            <TaxPreferences />
          </Route>
        )}
        {/* Activation flow routes */}
        <PrivateRoute path={["/i/:arenaDomain/join/verifyTwoFA", "/lp/join/verifyTwoFA"]}>
          <VerifyTwoFA />
        </PrivateRoute>
        <PrivateRoute path={["/i/:arenaDomain/link/verifyTwoFA", "/lp/link/verifyTwoFA"]}>
          <LinkVerifyTwoFA />
        </PrivateRoute>
        <PrivateRoute path={["/i/:arenaDomain/join/setUpTwoFA", "/lp/join/setUpTwoFA"]}>
          <SetUpTwoFA />
        </PrivateRoute>
        <PrivateRoute path={["/i/:arenaDomain/upgrade/setUpTwoFA", "/lp/upgrade/setUpTwoFA"]}>
          <UpgradeTwoFASetup />
        </PrivateRoute>
        <PrivateRoute path={["/i/:arenaDomain/upgrade/verifyTwoFA", "/lp/upgrade/verifyTwoFA"]}>
          <UpgradeTwoFAVerify />
        </PrivateRoute>
        <PrivateRoute path={["/i/:arenaDomain/newManagers", "/lp/newManagers"]}>
          <NewManagersList />
        </PrivateRoute>
        <PrivateRoute path={["/i/:arenaDomain/newInvitations", "/lp/newInvitations"]}>
          <NewManagerInvitations />
        </PrivateRoute>
        <PrivateRoute path={["/i/:arenaDomain/recovery", "/lp/recovery"]}>
          <RecoveryCodes />
        </PrivateRoute>
        <PrivateRoute path={["/i/:arenaDomain/editProfile", "/lp/editProfile"]}>
          <EditLPProfile />
        </PrivateRoute>
        <PrivateRoute path={["/i/:arenaDomain/terms-and-privacy-nn/:arena"]}>
          <TermsAndPolicy isAuthenticated noNav singleArena />
        </PrivateRoute>
        <PrivateRoute path="/i/:arenaDomain/compliance-remediation/position/:positionId">
          <ComplianceRemediationWrapper />
        </PrivateRoute>
        <PrivateRoute path="/i/profile-refresh/:profileGuid">
          <ComplianceRefreshComponentWrapper />
        </PrivateRoute>
        <PrivateRoute path="/i/document-refresh/:profileGuid">
          <ComplianceDocumentExpiryWrapper />
        </PrivateRoute>
        {/* Protected routes */}
        <PrivateRoute
          path={[
            getLpPortalTermsAndPrivacyPath(),
            `${LP_PORTAL_BASE_PATH}/terms-and-privacy/:arena`,
          ]}
          spoofingRedirectTo={spoofingDefaultRedirect}
        >
          <NavBar baseUrl="/lp" navItems={navItems} />
          <TermsAndPolicy isAuthenticated />
        </PrivateRoute>
        <PrivateRoute
          exact
          path={[
            `${LP_PORTAL_BASE_PATH}/terms-and-privacy`,
            `${LP_PORTAL_BASE_PATH}/terms-and-privacy/jsq/`,
          ]}
          spoofingRedirectTo={spoofingDefaultRedirect}
        >
          <Redirect to={getLpPortalTermsAndPrivacyPath()} />
        </PrivateRoute>
        <PrivateRoute
          path={`${LP_PORTAL_BASE_PATH}/documents/preview/:id`}
          spoofingRedirectTo={spoofingDefaultRedirect}
        >
          <ActivationProtectedRoute>
            <PreviewPage />
          </ActivationProtectedRoute>
        </PrivateRoute>
        <PrivateRoute path="/i/:arenaDomain">
          <ActivationProtectedRoute>
            <SingleManager />
          </ActivationProtectedRoute>
        </PrivateRoute>
        <Route exact path="/session_exchange/error">
          <DuplicateSession />
        </Route>
        <PrivateRoute path={LP_PORTAL_BASE_PATH} spoofingRedirectTo={spoofingDefaultRedirect}>
          <ActivationProtectedRoute>
            <MultiManager />
          </ActivationProtectedRoute>
        </PrivateRoute>
        <Redirect to={LP_PORTAL_BASE_PATH} />
      </Switch>
    </>
  );
  return (
    <FeaturesProvider
      value={{
        ...featureFlagData.devFeatures,
        ...(arenaFlagsData?.arenaFeatures ?? {}),
      }}
    >
      <AppContext.Provider
        // needs to be re-written
        // eslint-disable-next-line react/jsx-no-constructed-context-values
        value={{
          user,
          application: getCurrentApplication(arenaDomain),
          managerArenas,
          currentArenaContent,
          onlyOneGp,
          getLpPortalSettingsPath: () => getPath("settings"),
          getLpPortalSettingsAccountDetailsPath: () => getPath("settings/accounts"),
        }}
      >
        {featureFlagData.devFeatures.enableCoBrowseFeature ? (
          <UpscopeProvider apiKey="SqzNmzzieU" enabled>
            {routeProvider}
          </UpscopeProvider>
        ) : (
          routeProvider
        )}
      </AppContext.Provider>
    </FeaturesProvider>
  );
}

function PortalRouterWrapper() {
  return (
    <ErrorBoundary isFullPage>
      <Router history={history}>
        <UnleashFeatureFlagProvider applicationName="portal">
          <PortalWrapper />
        </UnleashFeatureFlagProvider>
      </Router>
    </ErrorBoundary>
  );
}

export const Portal = withDataProvider(PortalRouterWrapper, lpPortalClient);
